import type { ReactElement } from "react";
import { Button, Select, Tag, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { BidAssociation } from "design-scope";
import { Field, Section } from "./scope-item-field";

export type BidPackageOption = { value: string; label: string };

export type ScopeBidPackagesCardsEditorProps = {
  bids: BidAssociation[];
  onBidsChange: (next: BidAssociation[]) => void;
  bpOptions: BidPackageOption[];
  /** Overrides empty-list helper text. */
  emptyMessage?: string;
  addTestId?: string;
  sectionTitle?: string;
  sectionInfo?: string;
  /**
   * Optional dropdown footer / header (e.g. suggested vs all bid packages
   * toggle in create/edit). Receives the default menu nodes.
   */
  selectPopupRender?: (menu: ReactElement) => ReactElement;
};

/** Bids that have a bid package selected (persistable rows). */
export function pendingBidAssociations(
  bids: BidAssociation[]
): BidAssociation[] {
  return bids.filter((b) => b.bidPackageId);
}

function AddButton(props: { testId: string; onClick: () => void }) {
  return (
    <Button
      size="small"
      icon={<PlusOutlined />}
      data-testid={props.testId}
      onClick={props.onClick}
    >
      Add
    </Button>
  );
}

/**
 * Card-style bid package list (Primary / Set as Primary / remove) shared by
 * Create/Edit Scope Item and Bulk Link to Bid Package.
 */
export default function ScopeBidPackagesCardsEditor(
  props: ScopeBidPackagesCardsEditorProps
) {
  const {
    bids,
    onBidsChange,
    bpOptions,
    emptyMessage = "No Bid Packages linked yet.",
    addTestId = "bid-add",
    sectionTitle = "Link Bid Packages",
    sectionInfo = "The primary Bid Package is the one that carries this scope",
    selectPopupRender
  } = props;

  const selectedBidIds = new Set(
    bids.map((b) => b.bidPackageId).filter(Boolean)
  );

  const bpOptionsForRow = (rowIndex: number) =>
    bpOptions.filter(
      (o) =>
        o.value === bids[rowIndex]?.bidPackageId || !selectedBidIds.has(o.value)
    );

  const patchBid = (i: number, patch: Partial<BidAssociation>) => {
    onBidsChange(bids.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  };

  return (
    <Section
      title={sectionTitle}
      info={sectionInfo}
      action={
        <AddButton
          testId={addTestId}
          onClick={() =>
            onBidsChange([
              ...bids,
              {
                bidPackageId: "",
                role: "",
                primary: bids.length === 0
              }
            ])
          }
        />
      }
    >
      <div className="flex flex-col gap-3">
        {bids.map((b, i) => (
          <div key={i} className="dsm-link-card rounded-lg p-3">
            <Field
              label="Bid Package"
              extra={
                b.primary ? (
                  <Tag className="dsm-primary-tag ml-1">Primary</Tag>
                ) : (
                  <Button
                    color="default"
                    variant="dashed"
                    size="small"
                    icon={<PlusOutlined />}
                    className="dsm-set-primary-btn ml-1"
                    data-testid={`bid-primary-${i}`}
                    onClick={() =>
                      onBidsChange(
                        bids.map((x, idx) => ({
                          ...x,
                          primary: idx === i
                        }))
                      )
                    }
                  >
                    Set as Primary
                  </Button>
                )
              }
              action={
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  data-testid={`bid-remove-${i}`}
                  onClick={() =>
                    onBidsChange(bids.filter((_, idx) => idx !== i))
                  }
                />
              }
            >
              <Select
                showSearch
                optionFilterProp="label"
                data-testid={`bid-bp-${i}`}
                className="w-full"
                placeholder="Select"
                value={b.bidPackageId || undefined}
                options={bpOptionsForRow(i)}
                onChange={(bidPackageId) => patchBid(i, { bidPackageId })}
                popupRender={
                  selectPopupRender
                    ? (menu) => selectPopupRender(menu)
                    : undefined
                }
              />
            </Field>
          </div>
        ))}
        {bids.length === 0 && (
          <Typography.Text type="secondary">{emptyMessage}</Typography.Text>
        )}
      </div>
    </Section>
  );
}
